// Phase-3 Slice 36 — Monaco RenameProvider for cursor-on-identifier.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 36 + § NAV1 (amended
// 2026-08-23).
//
// F2 / "Rename Symbol" opens Monaco's inline rename box. ONE keybinding, two
// destinations decided by what the cursor names:
//
//   • ALIAS or CTE (buffer-local, no catalog object behind it) → this provider
//     returns a REAL `WorkspaceEdit` and Monaco applies it to the active model
//     through its bulk-edit service, which brackets the splice in
//     `pushStackElement()` — one native Ctrl+Z restores the buffer. No dialog,
//     no database round-trip.
//
//   • TABLE / VIEW / COLUMN (a persisted object) → unchanged: the preview
//     dialog opens as a side-effect and this provider returns an EMPTY
//     WorkspaceEdit, because the buffer edit for those happens later, after
//     the ALTERs actually succeed (see `renameService`'s open-tab rewrite).
//
// Rejections use Monaco's documented `Rejection` channel (verified against
// monaco-editor 0.44's `contrib/rename/browser/rename.js`):
//   - `resolveRenameLocation` → `MessageController.showMessage` renders the
//     reason inline at the cursor BEFORE the input box opens.
//   - `provideRenameEdits`    → `INotificationService.info` toasts the reason
//     AFTER the user submits a name. That is the right channel for the
//     collision guard, which can only be evaluated once a name exists.
//
// Dispatch order at the cursor:
//   1. Buffer-local binding (alias / CTE).
//   2. Column binding (alias-qualified or 2/3-part chain).
//   3. Table / view binding via the OE-tree lookup.
//
// Cross-database targets: what the cursor NAMES decides the database, not the
// tab — `B.dbo.users` typed from an `A` tab is previewed against `B`'s
// identifier index and its ALTERs run routed to `B`. Rename shares the routing
// policy with find-references (`find-references/referenceTargetAtCursor.ts`'s
// `routedDatabase`), so both surfaces answer "which database?" the same way.
// This matters most on SQL Server, whose rename ALTER is
// `EXEC sp_rename '[dbo].[users]'` — it carries no database of its own, so the
// connection-level routing is the only thing that decides which server-side
// object gets renamed.
//
// COLD CASE POLICY. Which tree node the cursor binds to depends on how the
// server compares names, and on a connection whose index has not been built
// that has not been probed yet. This provider therefore waits for the target
// database's case policy and RE-RESOLVES the cursor before it opens the preview
// — see `openOncePolicyKnown`. `resolveRenameLocation` stays synchronous: it
// only decides whether F2 opens its input box at all and what to pre-fill it
// with, and Monaco calls it on the keystroke path; the binding that MUTATES is
// the one `provideRenameEdits` acts on.

import type { languages, Position, IRange } from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import { findColumnBindingAtCursor } from '../go-to/columnAtCursor';
import { findTableViewHitAtCursor } from '../go-to/identifierAtCursor';
import { buildAliasResolver } from '../go-to/bufferAliasResolver';
import { routedDatabase } from '../find-references/referenceTargetAtCursor';
import {
  captureContext,
  contextHeldStill,
  resolveBehindPolicyWait,
  type InvocationContext,
} from '../find-references/invocationContext';
import { casePolicyReadyFor } from '../connection-lifecycle/navigationalLifecycle';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';

import type { QualifiedName, RenameKind, RenameRequest } from '../types';
import { typedDestinationIdentifier } from './renameDestination';
import { useRenamePreviewStore } from './renamePreviewStore';
import { trackNavFeature } from '../analytics/trackNavFeature';
import { getMysqlLowerCaseTableNamesFor } from '../mysqlCaseProbe';
import {
  offsetsToRange,
  provideBufferLocalEdits,
  resolveBufferTarget,
  type RenameModelLike,
} from './renameProvider.bufferLocal';

export type RenameProviderDeps = {
  readonly engineId: DatabaseEngineId;
  readonly getConnectionId?: () => string | null;
  readonly getDatabase?: () => string | null;
  /** Override the side-effecting dialog opener. Defaults to
   *  `useRenamePreviewStore.getState().openPreview`. */
  readonly openPreview?: (
    request: RenameRequest,
    ctx: { connectionId: string; database: string; tabDatabase?: string },
  ) => void;
  /** Wait until the CASE POLICY of the database the cursor named is known —
   *  the gate the lifecycle opens before it keys anything, without the catalog
   *  build behind it. Defaults to `casePolicyReadyFor`; see
   *  {@link openOncePolicyKnown} for why the target is re-resolved behind it. */
  readonly awaitCasePolicy?: (connectionId: string, database: string) => Promise<void>;
};

type Outcome = 'no-binding' | 'opened';

/** Monaco renders this in a small tooltip when F2 fires on a cursor that names
 *  nothing renamable — keep it to one line. */
export const RENAME_REJECT_REASON =
  'Nothing renamable at cursor — rename works on tables, views, columns, aliases and CTE names.';

type ResolvedTarget = {
  readonly kind: RenameKind;
  readonly fromIdentifier: QualifiedName;
  /** When `kind === 'column'` this is the column's host table/view. */
  readonly containingTable?: QualifiedName;
  /** The database this rename is planned, indexed and EXECUTED against — the
   *  one the cursor's chain spelled out (`B.dbo.users` typed from an `A` tab →
   *  `B`, in the tree's catalog spelling), else the tab's. Decided by the same
   *  `routedDatabase` policy find-references routes with; see there. */
  readonly database: string;
};

type ModelLike = RenameModelLike;

export function createRenameProvider(
  deps: RenameProviderDeps,
): languages.RenameProvider {
  return {
    resolveRenameLocation: (model, position) => {
      const local = resolveBufferTarget(deps.engineId, model, position);
      if (local) {
        return { range: offsetsToRange(model, local.cursorTokenRange), text: local.name };
      }
      const target = resolveTargetAtCursor(deps, model, position);
      if (!target) {
        // Say WHY, not just "no". Buffer-local names (aliases, CTEs) ARE
        // renamable now; what is still rejected is a keyword, a literal, or an
        // identifier the resolver cannot bind at all.
        return { rejectReason: RENAME_REJECT_REASON } as unknown as languages.RenameLocation;
      }
      const range = wordRange(model, position) ?? cursorPointRange(position);
      return { range, text: target.fromIdentifier.name };
    },
    provideRenameEdits: (model, position, newName) => {
      const local = provideBufferLocalEdits(deps.engineId, model, position, newName);
      if (local) return local;
      handleProvideRenameEdits(deps, model, position, newName);
      // Persisted objects keep the NAV1-era contract: the buffer is not touched
      // here. It is rewritten after the ALTERs succeed, undoably, per open tab.
      return { edits: [] } as languages.WorkspaceEdit;
    },
  };
}

// ─── Persisted objects (table / view / column) ──────────────────────────────

export function handleProvideRenameEdits(
  deps: RenameProviderDeps,
  model: ModelLike,
  position: Position,
  newName: string,
): Outcome {
  const target = resolveTargetAtCursor(deps, model, position);
  if (!target) return 'no-binding';
  const trimmed = newName.trim();
  if (trimmed.length === 0 || trimmed === target.fromIdentifier.name) return 'no-binding';

  const connectionId = deps.getConnectionId?.() ?? null;
  if (!connectionId) return 'no-binding';

  // `'opened'` says the cursor named something renamable and a preview is on
  // its way — the dialog itself opens once the case policy is in (below), a
  // microtask or two later. Several cases open nothing: a target the learnt
  // policy rules out (precisely the rename that must not happen), an invocation
  // context that moved under the wait, a gate that refused for a reason which
  // leaves the binding unverified, and a database that never settles — see
  // {@link openOncePolicyKnown} and `find-references/invocationContext.ts`.
  void openOncePolicyKnown(
    deps,
    model,
    position,
    trimmed,
    captureContext(deps, model, connectionId),
    target,
  );
  return 'opened';
}

/**
 * Re-resolve the cursor once the target database's CASE POLICY is known, then
 * open the preview for whatever it binds now.
 *
 * The resolvers above walk the OE tree synchronously, and every name
 * comparison in that walk asks how the server tells `dbo` from `DBO`
 * (`go-to/oeTreeQualification.ts`). On a connection whose index has not been
 * built the answer has not been probed, so the walk falls back to
 * case-INSENSITIVE: in a `_CS_` database a typed `B.dbo.users` is satisfied by
 * `B.DBO.users` as well, and a spelling the database does not hold at all is
 * satisfied by whatever the tree lists first. The exact-spelling preference
 * separates the first pair; nothing probe-free can separate the second. The
 * preview then planned — and Apply renamed — an object the user never named.
 *
 * Re-resolving, rather than making the cursor path async everywhere, because
 * the walk is a cheap in-memory pass over a tree that is already loaded, while
 * `resolveRenameLocation`, go-to and Peek all call the same resolvers on the
 * keystroke path. The wait is the POLICY only, never the catalog build: at most
 * two one-row probes, shared with the index wait the preview is about to make
 * anyway, so the dialog is not held back by a full index build.
 *
 * WHAT A REFUSED GATE MEANS depends on WHY it refused, and the other two cursor
 * surfaces answer it identically (`find-references/invocationContext.ts` holds
 * the table and the argument). `'no-lifecycle'` — the mock engine, an engine
 * the app does not index, a connection that left the store — proves there is no
 * policy to learn and no index behind it either, so the cursor's own binding
 * stands and the preview's own index wait reports the failure
 * (`renamePlanner` → `'no-index'`). Every other reason leaves the binding
 * UNVERIFIED while `planRename`'s own `indexReadyFor` retries and may succeed
 * the second time — planning, and on Apply renaming, the case-different
 * neighbour the cold walk picked. Those open NOTHING.
 *
 * A DATABASE the re-resolution MOVES to is re-verified rather than trusted: its
 * own collation is what the first wait never probed, so the loop waits for it
 * and walks the cursor again, bounded.
 *
 * ABORTING when the INVOCATION CONTEXT moved — the buffer, the connection or
 * the database — for the reasons `invocationContext.ts` records. There is no
 * safe merge of two contexts, so the rename opens NOTHING: a preview the user
 * can re-open with one keystroke is recoverable, a preview whose Apply button
 * executes against the wrong object is not.
 */
async function openOncePolicyKnown(
  deps: RenameProviderDeps,
  model: ModelLike,
  position: Position,
  newName: string,
  captured: InvocationContext,
  cursorTarget: ResolvedTarget,
): Promise<void> {
  const awaitPolicy = deps.awaitCasePolicy ?? casePolicyReadyFor;
  const resolution = await resolveBehindPolicyWait<ResolvedTarget>({
    cursorTarget,
    // The CAPTURED connection, not a fresh read: `heldStill` is what proves the
    // two are still the same, and pairing a new connection's binding with the
    // old id is the mismatch this whole guard exists to stop.
    awaitPolicy: (target) => awaitPolicy(captured.connectionId, target.database),
    resolve: () => resolveTargetAtCursor(deps, model, position),
    heldStill: () => contextHeldStill(deps, model, captured),
  });
  if (resolution.kind === 'abort') return;
  const target = resolution.target;
  if (newName === target.fromIdentifier.name) return;

  const request: RenameRequest = {
    kind: target.kind,
    fromIdentifier: target.fromIdentifier,
    toIdentifier: makeToIdentifier(target.fromIdentifier, newName, deps.engineId),
    engineId: deps.engineId,
    containingTable: target.containingTable,
  };

  trackNavFeature({ name: 'feature:navfeatures_rename_invoke', props: { kind: request.kind, source: 'keybinding' } });
  const opener = deps.openPreview ?? defaultOpenPreview;
  // NOT the tab's database: a chain that NAMES one (`B.dbo.users` from an `A`
  // tab) is renamed in `B`. The preview plans against `B`'s identifier index,
  // and apply runs the ALTERs through `executeQueryV2({ database })` routed to
  // `B` — without which MSSQL's `sp_rename '[dbo].[users]'` would execute in
  // `A` and rename `A`'s same-named table.
  // `tabDatabase` is the routing the user can SEE (the tab's own binding, held
  // still by `contextHeldStill` above); the preview names both whenever it
  // differs from the database the rename actually runs in.
  opener(request, {
    connectionId: captured.connectionId,
    database: target.database,
    tabDatabase: captured.database ?? undefined,
  });
}

function defaultOpenPreview(
  request: RenameRequest,
  ctx: { connectionId: string; database: string; tabDatabase?: string },
): void {
  void useRenamePreviewStore.getState().openPreview({
    connectionId: ctx.connectionId,
    database: ctx.database,
    tabDatabase: ctx.tabDatabase,
    request,
    mysqlLowerCaseTableNames: getMysqlLowerCaseTableNamesFor(ctx.connectionId, request.engineId),
  });
}

function resolveTargetAtCursor(
  deps: RenameProviderDeps,
  model: ModelLike,
  position: Position,
): ResolvedTarget | null {
  const connectionId = deps.getConnectionId?.() ?? null;
  if (!connectionId) return null;

  const source = model.getValue();
  const offset = model.getOffsetAt(position);
  const database = deps.getDatabase?.() ?? null;
  const tabDatabase = database ?? '';

  const columnHit = findColumnBindingAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
    resolveAlias: buildAliasResolver({
      source,
      cursorOffset: offset,
      engine: databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql',
    }),
  });
  if (columnHit) {
    return {
      kind: 'column',
      fromIdentifier: columnHit.binding.column,
      containingTable: columnHit.binding.relation.qualified,
      database: routedDatabase(
        columnHit.explicitDatabase,
        tabDatabase,
        columnHit.binding.relation.qualified.database,
      ),
    };
  }

  const relationHit = findTableViewHitAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
  });
  const relationBinding = relationHit?.binding;
  if (relationBinding && (relationBinding.kind === 'table' || relationBinding.kind === 'view')) {
    return {
      kind: relationBinding.kind,
      fromIdentifier: relationBinding.target,
      database: routedDatabase(
        relationHit?.explicitDatabase,
        tabDatabase,
        relationBinding.target.database,
      ),
    };
  }

  return null;
}

function makeToIdentifier(
  from: QualifiedName,
  newName: string,
  engineId: DatabaseEngineId,
): QualifiedName {
  // The box takes free text, delimiters and all — `"MixedCase"` reaches here
  // exactly as typed. `typedDestinationIdentifier` strips them ONCE and keeps
  // the intent on `quoting`, so the schema ALTER delimits the decoded name
  // instead of wrapping the user's delimiters a second time.
  return typedDestinationIdentifier(from, newName, databaseEngineIdToSqlEngine(engineId));
}

function wordRange(model: ModelLike, position: Position): IRange | null {
  const word = model.getWordAtPosition(position);
  if (!word) return null;
  return {
    startLineNumber: position.lineNumber,
    startColumn: word.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: word.endColumn,
  };
}

function cursorPointRange(position: Position): IRange {
  return {
    startLineNumber: position.lineNumber,
    startColumn: position.column,
    endLineNumber: position.lineNumber,
    endColumn: position.column,
  };
}
